import React, { useCallback, useEffect, useMemo, useState } from 'react';
import * as adminApi from '../api/adminApi';
import { PageHeader, Card, CardBody, ErrorBanner } from '../components/PageChrome';
import { DateRangeChips } from '../components/analytics/DateRangeChips';
import { AnalyticsDualLineChart, AnalyticsChartSummary } from '../components/analytics/AnalyticsDualLineChart';
import { formatCustomPeriodLabel } from '../lib/analyticsPresets';
import { pickErrorMessage } from '../lib/apiHelpers';
import { formatMoney, formatPlacedDate } from '../lib/format';
import { CommissionFlowLink } from '../components/CommissionFlowLink';

const EMPTY_ANALYTICS = {
  periodLabel: '',
  summary: {
    ordersCount: 0,
    grossMerchandise: 0,
    commissionCollected: 0,
    cafeSettled: 0,
    avgCommissionPerOrder: 0,
  },
  timeline: [],
  recentOrders: [],
};

function SummaryStat({ label, value, hint }) {
  return (
    <div className="col-6 col-lg-4 col-xl">
      <div className="commission-stat h-100">
        <div className="commission-stat__label">{label}</div>
        <div className="commission-stat__value tabular-nums">{value}</div>
        {hint ? <div className="commission-stat__hint small text-secondary">{hint}</div> : null}
      </div>
    </div>
  );
}

function defaultCustomDates() {
  const to = new Date();
  const from = new Date();
  from.setDate(from.getDate() - 3);
  return {
    from: from.toISOString().slice(0, 10),
    to: to.toISOString().slice(0, 10),
  };
}

export default function Analytics() {
  const [rangeId, setRangeId] = useState('today');
  const [customFrom, setCustomFrom] = useState(() => defaultCustomDates().from);
  const [customTo, setCustomTo] = useState(() => defaultCustomDates().to);
  const [data, setData] = useState(EMPTY_ANALYTICS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = { preset: rangeId };
      if (rangeId === 'custom') {
        params.dateFrom = customFrom;
        params.dateTo = customTo;
      }
      const res = await adminApi.fetchAnalytics(params);
      setData({
        periodLabel: res.periodLabel ?? '',
        summary: res.summary ?? EMPTY_ANALYTICS.summary,
        timeline: res.timeline ?? [],
        recentOrders: res.recentOrders ?? [],
      });
    } catch (err) {
      setError(pickErrorMessage(err));
      setData(EMPTY_ANALYTICS);
    } finally {
      setLoading(false);
    }
  }, [rangeId, customFrom, customTo]);

  useEffect(() => {
    if (rangeId === 'custom' && (!customFrom || !customTo)) return;
    load();
  }, [load, rangeId, customFrom, customTo]);

  const periodLabel = useMemo(() => {
    if (rangeId === 'custom') {
      const from = customFrom ? new Date(`${customFrom}T12:00:00`) : null;
      const to = customTo ? new Date(`${customTo}T12:00:00`) : null;
      return data.periodLabel || formatCustomPeriodLabel(from, to);
    }
    return data.periodLabel || '—';
  }, [rangeId, data.periodLabel, customFrom, customTo]);

  const handleRangeChange = (id) => {
    setRangeId(id);
  };

  return (
    <>
      <PageHeader
        title="Analytics"
        subtitle="Store performance and commission for the selected period"
        actions={
          <span className="badge rounded-pill text-bg-light border px-3 py-2 font-sans text-secondary">
            {periodLabel}
          </span>
        }
      />

      {error ? <ErrorBanner message={error} /> : null}

      <Card className="mb-4 border-0 shadow-sm">
        <CardBody className="p-3 p-md-4">
          <div className="d-flex flex-column flex-lg-row align-items-lg-center justify-content-between gap-3">
            <div>
              <div className="small text-secondary text-uppercase fw-semibold mb-2" style={{ letterSpacing: '0.06em' }}>
                Date range
              </div>
              <DateRangeChips activeId={rangeId} onChange={handleRangeChange} />
            </div>
            {rangeId === 'custom' ? (
              <div className="analytics-custom-dates d-flex flex-wrap align-items-end gap-2">
                <div>
                  <label htmlFor="analytics-from" className="form-label small text-secondary mb-1">
                    From
                  </label>
                  <input
                    id="analytics-from"
                    type="date"
                    className="form-control form-control-sm"
                    value={customFrom}
                    max={customTo || undefined}
                    onChange={(e) => setCustomFrom(e.target.value)}
                  />
                </div>
                <div>
                  <label htmlFor="analytics-to" className="form-label small text-secondary mb-1">
                    To
                  </label>
                  <input
                    id="analytics-to"
                    type="date"
                    className="form-control form-control-sm"
                    value={customTo}
                    min={customFrom || undefined}
                    onChange={(e) => setCustomTo(e.target.value)}
                  />
                </div>
              </div>
            ) : null}
          </div>
        </CardBody>
      </Card>

      {loading ? (
        <div className="text-center py-5 text-secondary">Loading analytics…</div>
      ) : (
        <>
          {!error && data.summary.ordersCount === 0 ? (
            <div className="alert alert-secondary border mb-4" role="status">
              No paid orders in <strong>{periodLabel}</strong>.
            </div>
          ) : null}
          <div className="row g-3 g-lg-4 mb-4">
            <SummaryStat label="Orders" value={data.summary.ordersCount} hint={periodLabel} />
            <SummaryStat label="Gross merchandise" value={formatMoney(data.summary.grossMerchandise)} hint="Paid by customers" />
            <SummaryStat
              label="Platform net"
              value={formatMoney(data.summary.commissionCollected)}
              hint="After 2.36% PG fee"
            />
            <SummaryStat label="Café settled" value={formatMoney(data.summary.cafeSettled)} hint="After base & delivery PG" />
            <SummaryStat label="Avg net / order" value={formatMoney(data.summary.avgCommissionPerOrder)} />
          </div>

          <Card className="mb-4 border-0 shadow-sm">
            <CardBody className="p-3 p-md-4">
              <AnalyticsDualLineChart points={data.timeline} periodLabel={periodLabel} />
              <AnalyticsChartSummary points={data.timeline} />
            </CardBody>
          </Card>


          <Card className="mt-4">
            <CardBody className="p-0">
              <div className="px-3 px-md-4 py-3 border-bottom">
                <h2 className="h6 text-secondary text-uppercase letter-spacing-tight mb-0">Recent orders</h2>
                <p className="small text-secondary mb-0 mt-1">{periodLabel}</p>
              </div>
              <div className="admin-table-shell mb-0">
                <div className="table-responsive">
                  <table className="table table-sm align-middle mb-0 admin-data-table">
                    <thead>
                      <tr>
                        <th scope="col">Order</th>
                        <th scope="col">Placed</th>
                        <th scope="col" className="text-end">
                          Order total
                        </th>
                        <th scope="col" className="text-end">
                          Platform net
                        </th>
                        <th scope="col" className="text-end">
                          Detail
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.recentOrders.length === 0 ? (
                        <tr>
                          <td colSpan={5} className="text-center text-secondary py-4">
                            No orders in this period
                          </td>
                        </tr>
                      ) : (
                        data.recentOrders.map((row) => (
                          <tr key={row.orderNo}>
                            <td className="fw-semibold text-dark">{row.orderNo}</td>
                            <td className="text-secondary small">{formatPlacedDate(row.placedAt)}</td>
                            <td className="text-end tabular-nums">{formatMoney(row.orderTotal)}</td>
                            <td className="text-end tabular-nums fw-semibold text-dark">{formatMoney(row.commission)}</td>
                            <td className="text-end">
                              <div className="table-actions">
                                <CommissionFlowLink orderNo={row.orderNo} from="/analytics" placedAt={row.placedAt} />
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </CardBody>
          </Card>
        </>
      )}
    </>
  );
}
